// useNotifications.js — WebSocket hook for real-time alerts
import { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth } from './api';

const SEVERITY_COLOR = {
  critical: { bg: '#FCEBEB', color: '#A32D2D', border: '#F09595', dot: '#E24B4A' },
  warning:  { bg: '#FAEEDA', color: '#854F0B', border: '#FAC775', dot: '#EF9F27' },
  info:     { bg: '#E6F1FB', color: '#0C447C', border: '#B5D4F4', dot: '#378ADD' },
  status:   { bg: '#EAF3DE', color: '#3B6D11', border: '#C0DD97', dot: '#639922' },
};

export function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [connected, setConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    const auth = getAuth();
    if (!auth) return;

    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
    console.log('[WS] Connecting to', wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        console.log('[WS] Connected');
        // Authenticate with server
        ws.send(JSON.stringify({ type: 'auth', account: auth.account, jsession: auth.jsession }));
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (msg.type === 'auth_ok') return;
          if (msg.type === 'connected') return;

          const notification = {
            id: msg.id || `${Date.now()}-${Math.random()}`,
            type: msg.type,
            severity: msg.severity || 'info',
            title: msg.title || 'Notification',
            vehicle: msg.vehicle || '',
            time: msg.time || new Date().toLocaleTimeString(),
            message: msg.message || '',
            speed: msg.speed,
            location: msg.location,
            lat: msg.lat,
            lng: msg.lng,
            picUrl: msg.picUrl,
            read: false,
            raw: msg
          };

          setNotifications(prev => {
            // Deduplicate
            if (prev.some(n => n.id === notification.id)) return prev;
            return [notification, ...prev].slice(0, 100); // keep last 100
          });
          setUnreadCount(c => c + 1);

          // Browser notification if permitted
          if (Notification.permission === 'granted') {
            new Notification(`MDVR — ${notification.title}`, {
              body: `${notification.vehicle} · ${notification.time}`,
              icon: '/favicon.ico'
            });
          }
        } catch (e) {}
      };

      ws.onclose = () => {
        setConnected(false);
        console.log('[WS] Disconnected, reconnecting in 5s...');
        reconnectTimer.current = setTimeout(connect, 5000);
      };

      ws.onerror = (err) => {
        console.error('[WS] Error:', err);
        ws.close();
      };
    } catch (e) {
      console.error('[WS] Failed to connect:', e);
      reconnectTimer.current = setTimeout(connect, 5000);
    }
  }, []);

  useEffect(() => {
    // Request browser notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
    connect();
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };
  }, [connect]);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  return { notifications, connected, unreadCount, markAllRead, clearAll, SEVERITY_COLOR };
}

// NotificationBell component
export function NotificationBell({ notifications, unreadCount, connected, markAllRead, clearAll, SEVERITY_COLOR }) {
  const [open, setOpen] = useState(false);

  const toggle = () => {
    setOpen(o => !o);
    if (!open && unreadCount > 0) markAllRead();
  };

  return (
    <div style={{ position: 'relative' }}>
      {/* Bell button */}
      <div onClick={toggle} style={{ cursor: 'pointer', position: 'relative', width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: open ? '#E6F1FB' : 'transparent', transition: 'background .12s' }}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill={unreadCount > 0 ? '#E24B4A' : '#888'}>
          <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
        </svg>
        {unreadCount > 0 && (
          <span style={{ position: 'absolute', top: 2, right: 2, width: 16, height: 16, background: '#E24B4A', borderRadius: '50%', fontSize: 9, color: 'white', fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>

      {/* Dropdown panel */}
      {open && (
        <div style={{ position: 'absolute', top: 38, right: 0, width: 340, background: 'white', border: '0.5px solid #e0e0e0', borderRadius: 12, zIndex: 1000, boxShadow: '0 4px 24px rgba(0,0,0,.10)', overflow: 'hidden' }}>
          {/* Header */}
          <div style={{ padding: '12px 14px', borderBottom: '0.5px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>Notifications</span>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: connected ? '#4CAF50' : '#E24B4A', display: 'inline-block' }} title={connected ? 'Live' : 'Reconnecting...'} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {notifications.length > 0 && (
                <span onClick={clearAll} style={{ fontSize: 11, color: '#999', cursor: 'pointer', padding: '2px 6px', borderRadius: 4, border: '0.5px solid #ddd' }}>Clear all</span>
              )}
            </div>
          </div>

          {/* Notification list */}
          <div style={{ maxHeight: 380, overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '24px 0', textAlign: 'center', color: '#bbb', fontSize: 12 }}>
                <div style={{ marginBottom: 6 }}>No notifications yet</div>
                <div style={{ fontSize: 10 }}>{connected ? 'Listening for alerts...' : 'Connecting...'}</div>
              </div>
            ) : notifications.map(n => {
              const sc = SEVERITY_COLOR[n.severity] || SEVERITY_COLOR.info;
              return (
                <div key={n.id} style={{ padding: '10px 14px', borderBottom: '0.5px solid #f5f5f5', background: n.read ? 'white' : '#fafcff', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: sc.dot, flexShrink: 0, marginTop: 4 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 500 }}>{n.title}</span>
                      <span style={{ fontSize: 10, color: '#bbb', whiteSpace: 'nowrap', marginLeft: 8 }}>{n.time}</span>
                    </div>
                    {n.vehicle && <div style={{ fontSize: 11, color: '#888' }}>{n.vehicle}{n.speed ? ` · ${n.speed} km/h` : ''}</div>}
                    {n.location && <div style={{ fontSize: 10, color: '#bbb', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.location}</div>}
                    {n.message && <div style={{ fontSize: 11, color: '#888' }}>{n.message}</div>}
                  </div>
                  <span style={{ background: sc.bg, color: sc.color, fontSize: 9, padding: '2px 5px', borderRadius: 5, fontWeight: 500, flexShrink: 0 }}>
                    {n.severity?.toUpperCase()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export { SEVERITY_COLOR };
